import React from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, Info, Film, EyeOff, SearchX, Layers, Tv, Users, Receipt, DollarSign } from 'lucide-react'
import { KPICard } from '../../ui'
import BroadcastPageHeader from './BroadcastPageHeader'
import BroadcastUnavailable from './BroadcastUnavailable'
import useBroadcastExposure from './useBroadcastExposure'
import { formatChannelType, describeViewers } from './broadcastCustomerFormat'
import { formatMoney } from '../broadcastValuation'

/** The model's scene enum, in the words a customer would use. */
const SCENE_LABELS: Record<string, string> = {
  live_play: 'Live play',
  between_play: 'Between play',
  replay: 'Replay',
  innings_break: 'Innings break',
  commercial: 'Commercial',
  crowd: 'Crowd',
  studio: 'Studio',
  graphic: 'Graphic',
  unknown: 'Unclassified'
}

const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0)

/**
 * What the broadcast itself looked like, as distinct from what any one brand
 * got out of it: where exposure landed, and how much signage we could not read.
 *
 * Both are properties of the footage rather than of a sponsor, which is why
 * they sit here and not under the brand cards.
 */
const BroadcastDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { data, loading, locked } = useBroadcastExposure(id)

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
    </div>
  }

  if (!data) return <BroadcastUnavailable locked={locked} />

  const { broadcast: b, brands, zones, coverage, frames, scenes } = data
  // Share is measured against the brands that count, not every row returned —
  // excluded and own-brand rows ship in the payload so they can be restored.
  const counted = brands.filter((x) => x.counts)
  const totalArea = counted.reduce((s, x) => s + Number(x.total_area_seconds), 0)
  const worstCoverage = coverage.filter((c) => c.pct_unreadable >= 50)

  // Signage seen across the whole broadcast, readable or not. Summed from the
  // per-zone rows the panel below already shows, so the headline and the
  // breakdown cannot disagree.
  const named = coverage.reduce((s, c) => s + c.named, 0)
  const unreadable = coverage.reduce((s, c) => s + c.unreadable, 0)

  // `frames` is null for a broadcast analysed before per-frame records
  // existed. That is NOT MEASURED, not zero — an em dash is the honest
  // rendering and a 0% would be a lie about a broadcast full of brands.
  const brandFree = frames ? frames.analysed - frames.withDetections : null
  const sceneTotal = scenes.reduce((s, x) => s + x.frames, 0)

  const currency = b.currency ?? 'USD'
  const effectiveCpm = b.effective_cpm ?? b.cpm

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <BroadcastPageHeader broadcast={b} view="details" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Frames Analysed"
          value={frames ? frames.analysed.toLocaleString() : '—'}
          icon={Film} colorScheme="slate"
          tooltip={frames
            ? 'Still frames sampled from the broadcast and read by the model.'
            : 'Not recorded for this broadcast. Per-frame records began after it was analysed; re-analysing captures them.'}
        />
        <KPICard
          label="Brand-Free"
          value={brandFree != null && frames
            ? `${Math.round(pct(brandFree, frames.analysed))}%`
            : '—'}
          icon={SearchX} colorScheme="indigo"
          tooltip="Share of sampled frames carrying no readable brand at all — commercials, crowd shots and tight action."
        />
        <KPICard
          label="Unreadable Signage"
          value={named + unreadable > 0
            ? `${Math.round(pct(unreadable, named + unreadable))}%`
            : '—'}
          icon={EyeOff} colorScheme="amber"
          tooltip="Signage visible in the broadcast but not legible at source resolution. Counted, attributed to no brand."
        />
        <KPICard
          label="Brands Per Frame"
          value={frames && frames.analysed > 0
            ? (frames.detections / frames.analysed).toFixed(1)
            : '—'}
          icon={Layers} colorScheme="blue"
          tooltip="Average readable brands on screen, across every sampled frame including the empty ones."
        />

        {/* Second row: who carried it, who watched, and what that audience
            is priced at. These are the inputs to every dollar figure on the
            Sponsor Exposure tab, so they are stated here in full. */}
        <KPICard
          label="Feed Provider"
          value={b.channel_name ?? '—'}
          icon={Tv} colorScheme="purple"
          tooltip={b.channel_name
            ? [formatChannelType(b.channel_type), b.feed_role ? `${b.feed_role} feed` : null]
                .filter(Boolean).join(' · ') || 'The feed provider this broadcast was distributed on.'
            : 'No feed provider recorded for this broadcast.'}
        />
        <KPICard
          label="Viewers"
          value={b.viewers == null
            ? '—'
            : `${Number(b.viewers).toLocaleString()}${b.viewers_is_estimate ? ' (est.)' : ''}`}
          icon={Users} colorScheme="blue"
          tooltip={b.viewers == null
            ? 'No audience recorded yet. Without it, exposure cannot be priced.'
            : `${describeViewers(b)}. The audience every dollar figure for this broadcast is priced from.`}
        />
        <KPICard
          label="CPM"
          value={effectiveCpm == null ? '—' : formatMoney(Number(effectiveCpm), currency)}
          icon={Receipt} colorScheme="slate"
          tooltip={effectiveCpm == null
            ? 'No CPM recorded and no rate card assigned.'
            : b.valuation_basis === 'rate_card'
              ? `From the rate card${b.rate_card_name ? ` "${b.rate_card_name}"` : ''}: cost per thousand viewers.`
              : 'Negotiated for this broadcast: cost per thousand viewers.'}
        />
        <KPICard
          label="Rate Per :30"
          value={b.rate_per_30s == null ? '—' : formatMoney(Number(b.rate_per_30s), currency)}
          icon={DollarSign} colorScheme="emerald"
          tooltip={b.rate_per_30s == null
            ? 'Needs both viewers and a CPM. Until then this broadcast shows exposure without value.'
            : 'Viewers / 1,000 x CPM: what one full-screen 30-second spot is worth on this broadcast.'}
        />
      </div>

      {scenes.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">What was on screen</h2>
          <p className="text-xs text-gray-500 mb-2.5">
            Every sampled frame classified, including those carrying no signage — so this
            is a share of the broadcast, not of the frames where a brand was found.
          </p>
          <div className="space-y-1.5">
            {scenes.map((sc) => {
              const share = pct(sc.frames, sceneTotal)
              return (
                <div key={sc.scene} className="flex items-center gap-2 text-xs">
                  <span className="w-40 text-gray-600 truncate">
                    {SCENE_LABELS[sc.scene] ?? sc.scene}
                  </span>
                  <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-sky-500" style={{ width: `${share}%` }} />
                  </div>
                  <span className="w-40 text-right text-gray-600 tabular-nums">
                    <span className="font-medium">{sc.frames.toLocaleString()}</span>
                    <span className="text-gray-400"> frames ({Math.round(share)}%)</span>
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {zones.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Exposure by placement</h2>
          <div className="space-y-1.5">
            {zones.map((z) => {
              const share = totalArea ? (Number(z.total_area_seconds) / totalArea) * 100 : 0
              return (
                <div key={z.zone_code} className="flex items-center gap-2 text-xs">
                  <span className="w-40 text-gray-600 truncate">{z.zone_label}</span>
                  <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500" style={{ width: `${share}%` }} />
                  </div>
                  <span className="w-40 text-right text-gray-600 tabular-nums">
                    <span className="font-medium">{Number(z.total_area_seconds).toFixed(2)}</span>
                    <span className="text-gray-400"> ({Math.round(share)}% · {z.brands} brands)</span>
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Shown deliberately. Reporting what we could read without saying what
          we could not favours close-up placements and hides that from the
          person paying for the numbers. */}
      {coverage.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">Signage we could not read</h2>
          <p className="text-xs text-gray-500 mb-2.5">
            Boards visible in the broadcast but not legible at source resolution.
            They are counted here and attributed to no brand, so exposure figures
            understate rather than guess.
          </p>
          <div className="space-y-1.5">
            {coverage.map((c) => (
              <div key={c.zone_code} className="flex items-center gap-2 text-xs">
                <span className="w-40 text-gray-600 truncate">{c.zone_label}</span>
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-emerald-500"
                       style={{ width: `${100 - c.pct_unreadable}%` }} />
                  <div className="h-full bg-amber-500"
                       style={{ width: `${c.pct_unreadable}%` }} />
                </div>
                <span className="w-44 text-right text-gray-600 tabular-nums">
                  <span className="font-medium">{c.unreadable}</span> of {c.named + c.unreadable} unreadable
                  <span className="text-gray-400"> ({c.pct_unreadable}%)</span>
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-3">
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-2 rounded-sm bg-emerald-500" /> read and attributed
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-2 rounded-sm bg-amber-500" /> present but unreadable
            </span>
          </div>
          {worstCoverage.length > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 mt-3 pt-3 border-t border-gray-100">
              <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Most signage was unreadable in {worstCoverage.map((c) => c.zone_label).join(', ')}.
                Exposure in {worstCoverage.length === 1 ? 'that placement is' : 'those placements are'} understated.
              </span>
            </p>
          )}
        </div>
      )}

      {zones.length === 0 && coverage.length === 0 && scenes.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 py-10 text-center text-sm text-gray-400">
          No placement or readability detail recorded for this broadcast
        </div>
      )}
    </div>
  )
}

export default BroadcastDetailsPage
